import { useState, type FormEvent } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, callable } from '@/lib/firebase';
import { normalizePhoneInput } from '@/lib/phone';
import { messageForAuthError } from '@/lib/authErrors';
import { Logo } from '@/components/Logo';
import { strings } from '@/strings';

const sendOtp = callable<{ phone: string }, { sent: boolean }>('sendOtp');
const verifyOtpAndSignIn = callable<
  { phone: string; code: string },
  { token: string; role: string; isNewUser: boolean }
>('verifyOtpAndSignIn');

/**
 * Two steps, one number. The phone is normalized here before it is sent so the
 * button can be honest about whether a number is usable, and so the server
 * receives the same canonical string the OTP record is keyed on.
 *
 * A failed verify keeps the person on the code step: the attempt counter is
 * server-side and bounded, and sending them back to the number field would
 * cost them a fresh SMS for a typo.
 */
export function SignInPage() {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canonical = normalizePhoneInput(phone);

  async function onSend(event: FormEvent) {
    event.preventDefault();
    if (!canonical || busy) return;
    setBusy(true);
    setError(null);
    try {
      await sendOtp({ phone: canonical });
      setStep('code');
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(event: FormEvent) {
    event.preventDefault();
    if (!canonical || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { token } = await verifyOtpAndSignIn({ phone: canonical, code });
      // The auth listener in AuthProvider picks it up and the router sends the
      // person to their own home; this page does not decide where that is.
      await signInWithCustomToken(auth, token);
    } catch (err) {
      setError(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-sm flex-col justify-center gap-6 p-6">
      <Logo withTagline size="lg" className="items-center self-center" />
      <h1 className="text-xl font-bold text-primary">{strings.signInTitle}</h1>

      {step === 'phone' ? (
        <form onSubmit={onSend} className="flex flex-col gap-4">
          <label className="flex flex-col gap-2 text-sm" htmlFor="phone">
            {strings.signInPhoneLabel}
            <input
              id="phone"
              inputMode="tel"
              autoComplete="tel"
              dir="ltr"
              className="rounded-md border border-border-subtle px-3 py-2 text-right"
              placeholder={strings.signInPhonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={!canonical || busy}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.signInSendCode}
          </button>
        </form>
      ) : (
        <form onSubmit={onVerify} className="flex flex-col gap-4">
          <p className="text-sm text-muted">{strings.signInCodeSent}</p>
          <label className="flex flex-col gap-2 text-sm" htmlFor="code">
            {strings.signInCodeLabel}
            <input
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              className="rounded-md border border-border-subtle px-3 py-2 text-center tracking-[0.5em]"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={code.length < 4 || busy}
            className="min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {strings.signInVerify}
          </button>
          <button
            type="button"
            className="min-h-11 self-start text-sm text-muted underline"
            onClick={() => { setStep('phone'); setCode(''); setError(null); }}
          >
            {strings.signInChangeNumber}
          </button>
        </form>
      )}

      {error ? <p role="alert" className="text-sm text-danger">{error}</p> : null}
    </main>
  );
}
